// One geography for the map and diagnostics. Sources and boundary conventions:
// docs/diagnostics-geography.md. Populations are people, not covered-place sums.
export interface PlaceGeography {
  readonly name: string;
  readonly scope: 'city' | 'nationwide' | 'regional';
  readonly population?: number;
  readonly urbanCenter?: string;
  readonly urbanCenterId?: number;
  readonly note?: string;
}
export interface Country {
  readonly name: string;
  readonly population: number;
  readonly iso3: string;
  readonly codes: readonly string[];
}
export interface Continent {
  readonly name: string;
  readonly population: number;
  readonly countries: readonly Country[];
}
export const PLACE_GEOGRAPHY: Readonly<Record<string, PlaceGeography>> = {
  gb: {
    name: 'Great Britain',
    scope: 'nationwide',
  },
  db: {
    name: 'Germany',
    scope: 'nationwide',
  },
  ch: {
    name: 'Switzerland',
    scope: 'nationwide',
    note: 'Passenger rail across Switzerland; border clipping is a coverage setting, not a city population.',
  },
  bern: {
    name: 'Bern',
    scope: 'city',
    population: 206716,
    urbanCenter: 'Berne (Bern)',
    urbanCenterId: 930,
  },
  zurich: { name: 'Zurich', scope: 'city', population: 689657, urbanCenter: 'Zurich', urbanCenterId: 1668 },
  basel: { name: 'Basel', scope: 'city', population: 333021, urbanCenter: 'Basel', urbanCenterId: 1046 },
  geneva: { name: 'Geneva', scope: 'city', population: 436587, urbanCenter: 'Geneva', urbanCenterId: 60 },
  lausanne: { name: 'Lausanne', scope: 'city', population: 293509, urbanCenter: 'Lausanne', urbanCenterId: 231 },
  lugano: { name: 'Lugano', scope: 'city', population: 93677, urbanCenter: 'Lugano', urbanCenterId: 1847 },
  no: {
    name: 'Norway',
    scope: 'nationwide',
  },
  se: {
    name: 'Sweden',
    scope: 'nationwide',
  },
  mal: {
    name: 'Skåne',
    scope: 'regional',
    note: 'Skåne regional coverage, including Malmö.',
  },
  tokyo: {
    name: 'Tokyo',
    scope: 'city',
    population: 33412512,
    urbanCenter: 'Tōkyō (Tokyo)',
    urbanCenterId: 6030,
  },
  pdx: {
    name: 'Portland',
    scope: 'city',
    population: 1397788,
    urbanCenter: 'Portland',
    urbanCenterId: 5157,
  },
  sea: {
    name: 'Seattle',
    scope: 'city',
    population: 1403495,
    urbanCenter: 'Seattle',
    urbanCenterId: 5386,
  },
  sfbay: {
    name: 'Bay Area',
    scope: 'regional',
    note: 'Multi-center Bay Area coverage; no sum of overlapping urban centers.',
  },
  yyc: {
    name: 'Calgary',
    scope: 'city',
    population: 1197110,
    urbanCenter: 'Calgary',
    urbanCenterId: 1126,
  },
  den: {
    name: 'Denver',
    scope: 'city',
    population: 2265994,
    urbanCenter: 'Denver',
    urbanCenterId: 6032,
  },
  amtrak: {
    name: 'Amtrak',
    scope: 'nationwide',
    note: 'US nationwide rail with routes into Canada; listed once under the United States.',
  },
  msp: {
    name: 'Minneapolis–St. Paul',
    scope: 'city',
    population: 1107243,
    urbanCenter: 'Minneapolis [Saint Paul]',
    urbanCenterId: 7302,
  },
  chi: {
    name: 'Chicago',
    scope: 'city',
    population: 3683385,
    urbanCenter: 'Chicago',
    urbanCenterId: 7747,
  },
  tor: {
    name: 'Toronto',
    scope: 'city',
    population: 5494431,
    urbanCenter: 'Toronto',
    urbanCenterId: 2925,
  },
  mtl: {
    name: 'Montréal',
    scope: 'city',
    population: 2668723,
    urbanCenter: 'Montreal',
    urbanCenterId: 3645,
  },
  phl: {
    name: 'Philadelphia',
    scope: 'city',
    population: 2232420,
    urbanCenter: 'Philadelphia',
    urbanCenterId: 8270,
  },
  nyc: {
    name: 'New York',
    scope: 'city',
    population: 13920148,
    urbanCenter: 'New York City',
    urbanCenterId: 8354,
  },
  bos: {
    name: 'Boston',
    scope: 'city',
    population: 1639797,
    urbanCenter: 'Boston',
    urbanCenterId: 8493,
  },
  lon: {
    name: 'London',
    scope: 'city',
    population: 10416420,
    urbanCenter: 'London',
    urbanCenterId: 5938,
  },
  mad: {
    name: 'Madrid',
    scope: 'city',
    population: 5583601,
    urbanCenter: 'Madrid',
    urbanCenterId: 3440,
  },
  bcn: {
    name: 'Barcelona',
    scope: 'city',
    population: 3982704,
    urbanCenter: 'Barcelona',
    urbanCenterId: 5068,
  },
  vlc: {
    name: 'Valencia',
    scope: 'city',
    population: 1390045,
    urbanCenter: 'Valencia',
    urbanCenterId: 4574,
  },
  bio: {
    name: 'Bilbao',
    scope: 'city',
    population: 741795,
    urbanCenter: 'Bilbao',
    urbanCenterId: 4021,
  },
  svq: {
    name: 'Seville',
    scope: 'city',
    population: 699504,
    urbanCenter: 'Seville',
    urbanCenterId: 2157,
  },
  ovd: {
    name: 'Asturias',
    scope: 'regional',
    note: 'Asturias regional coverage, including Oviedo and Gijón.',
  },
  sdr: {
    name: 'Santander',
    scope: 'city',
    population: 211730,
    urbanCenter: 'Santander',
    urbanCenterId: 3599,
  },
  eas: {
    name: 'San Sebastián',
    scope: 'city',
    population: 230220,
    urbanCenter: 'Donostia / San Sebastián',
    urbanCenterId: 4191,
  },
  zaz: {
    name: 'Zaragoza',
    scope: 'city',
    population: 585533,
    urbanCenter: 'Zaragoza',
    urbanCenterId: 4388,
  },
  alc: {
    name: 'Murcia and Alicante',
    scope: 'regional',
    note: 'Murcia and Alicante coverage; no combined city population.',
  },
  agp: {
    name: 'Málaga',
    scope: 'city',
    population: 521854,
    urbanCenter: 'Málaga',
    urbanCenterId: 3224,
  },
  xry: {
    name: 'Cádiz',
    scope: 'city',
    population: 117874,
    urbanCenter: 'Cádiz',
    urbanCenterId: 1654,
  },
  lis: {
    name: 'Lisbon',
    scope: 'city',
    population: 1913798,
    urbanCenter: 'Lisboa (Lisbon)',
    urbanCenterId: 217,
  },
  rom: {
    name: 'Rome',
    scope: 'city',
    population: 2376474,
    urbanCenter: 'Roma (Rome)',
    urbanCenterId: 4156,
  },
  nl: {
    name: 'Netherlands',
    scope: 'nationwide',
  },
  ber: {
    name: 'Berlin',
    scope: 'city',
    population: 3560908,
    urbanCenter: 'Berlin',
    urbanCenterId: 5576,
  },
  fra: {
    name: 'Frankfurt',
    scope: 'city',
    population: 900483,
    urbanCenter: 'Frankfurt',
    urbanCenterId: 3500,
  },
  ham: {
    name: 'Hamburg',
    scope: 'city',
    population: 1759377,
    urbanCenter: 'Hamburg',
    urbanCenterId: 4278,
  },
  muc: {
    name: 'Munich',
    scope: 'city',
    population: 1815022,
    urbanCenter: 'Munich',
    urbanCenterId: 5337,
  },
  str: {
    name: 'Stuttgart',
    scope: 'city',
    population: 899103,
    urbanCenter: 'Stuttgart',
    urbanCenterId: 4113,
  },
  cgn: {
    name: 'Cologne',
    scope: 'city',
    population: 1125292,
    urbanCenter: 'Cologne',
    urbanCenterId: 1160,
  },
  ruhr: {
    name: 'Rhine-Ruhr',
    scope: 'regional',
    note: 'Multi-center Rhine-Ruhr coverage; no sum of urban centers.',
  },
  han: {
    name: 'Hannover',
    scope: 'city',
    population: 611288,
    urbanCenter: 'Hanover',
    urbanCenterId: 4316,
  },
  nue: {
    name: 'Nuremberg',
    scope: 'city',
    population: 644169,
    urbanCenter: 'Nuremberg',
    urbanCenterId: 5071,
  },
  lej: {
    name: 'Leipzig',
    scope: 'city',
    population: 542313,
    urbanCenter: 'Leipzig',
    urbanCenterId: 5391,
  },
  drs: {
    name: 'Dresden',
    scope: 'city',
    population: 554612,
    urbanCenter: 'Dresden',
    urbanCenterId: 5626,
  },
  rnk: {
    name: 'Rhine-Neckar',
    scope: 'regional',
    note: 'Multi-center Rhine-Neckar coverage; no sum of urban centers.',
  },
  kar: {
    name: 'Karlsruhe',
    scope: 'city',
    population: 298556,
    urbanCenter: 'Karlsruhe',
    urbanCenterId: 3394,
  },
  bre: {
    name: 'Bremen',
    scope: 'city',
    population: 422414,
    urbanCenter: 'Bremen',
    urbanCenterId: 3231,
  },
  fre: {
    name: 'Freiburg',
    scope: 'city',
    population: 234311,
    urbanCenter: 'Freiburg im Breisgau',
    urbanCenterId: 2814,
  },
  agb: {
    name: 'Augsburg',
    scope: 'city',
    population: 344255,
    urbanCenter: 'Augsburg',
    urbanCenterId: 5134,
  },
  prg: {
    name: 'Prague',
    scope: 'city',
    population: 1197765,
    urbanCenter: 'Praha (Prague)',
    urbanCenterId: 542,
  },
  vie: {
    name: 'Vienna',
    scope: 'city',
    population: 2031326,
    urbanCenter: 'Wien (Vienna)',
    urbanCenterId: 1054,
  },
  gdn: {
    name: 'Gdańsk',
    scope: 'city',
    population: 384985,
    urbanCenter: 'Gdansk',
    urbanCenterId: 2093,
  },
  krk: {
    name: 'Kraków',
    scope: 'city',
    population: 699015,
    urbanCenter: 'Krakow',
    urbanCenterId: 3608,
  },
  poz: {
    name: 'Poznań',
    scope: 'city',
    population: 494839,
    urbanCenter: 'Poznan',
    urbanCenterId: 1381,
  },
  waw: {
    name: 'Warsaw',
    scope: 'city',
    population: 1882756,
    urbanCenter: 'Warszawa (Warsaw)',
    urbanCenterId: 3660,
  },
  vce: {
    name: 'Venice',
    scope: 'city',
    population: 57048,
    urbanCenter: 'Venice',
    urbanCenterId: 3850,
  },
  man: {
    name: 'Manchester',
    scope: 'city',
    population: 2535427,
    urbanCenter: 'Manchester',
    urbanCenterId: 3388,
  },
  dub: {
    name: 'Ireland',
    scope: 'regional',
    note: 'Ireland and Northern Ireland, including Belfast; listed once under Ireland, not a population sum.',
  },
  osl: {
    name: 'Oslo',
    scope: 'city',
    population: 874991,
    urbanCenter: 'Oslo',
    urbanCenterId: 931,
  },
  hel: {
    name: 'Helsinki',
    scope: 'city',
    population: 1014317,
    urbanCenter: 'Helsinki (Helsingfors)',
    urbanCenterId: 555,
  },
  sto: {
    name: 'Stockholm',
    scope: 'city',
    population: 1500093,
    urbanCenter: 'Stockholm',
    urbanCenterId: 1672,
  },
  got: {
    name: 'Gothenburg',
    scope: 'city',
    population: 562945,
    urbanCenter: 'Gothenburg',
    urbanCenterId: 66,
  },
  bud: {
    name: 'Budapest',
    scope: 'city',
    population: 1609260,
    urbanCenter: 'Budapest',
    urbanCenterId: 942,
  },
  par: {
    name: 'Paris',
    scope: 'city',
    population: 9381921,
    urbanCenter: 'Paris',
    urbanCenterId: 2994,
  },
  fr: {
    name: 'France',
    scope: 'nationwide',
    note: 'National rail and admitted local/regional buses, including territorial and cross-border sources; listed once under France.',
  },
  hk: {
    name: 'Hong Kong',
    scope: 'city',
    population: 4749543,
    urbanCenter: 'Tamar (Hong Kong)',
    urbanCenterId: 11566,
  },
  per: {
    name: 'Perth',
    scope: 'city',
    population: 1079374,
    urbanCenter: 'Perth',
    urbanCenterId: 330,
  },
  adl: {
    name: 'Adelaide',
    scope: 'city',
    population: 879249,
    urbanCenter: 'Adelaide',
    urbanCenterId: 1003,
  },
  mel: {
    name: 'Melbourne',
    scope: 'city',
    population: 3639893,
    urbanCenter: 'Melbourne',
    urbanCenterId: 1634,
  },
  cbr: {
    name: 'Canberra',
    scope: 'regional',
    note: 'Canberra and ACT coverage; WUP2025 has no qualifying Canberra urban center. No administrative-population substitute.',
  },
  nsw: {
    name: 'Sydney',
    scope: 'regional',
    note: 'Sydney and New South Wales statewide transit, not a Sydney city population.',
  },
  bne: {
    name: 'Brisbane',
    scope: 'city',
    population: 1071628,
    urbanCenter: 'Brisbane',
    urbanCenterId: 2591,
  },
};
export const POPULATION_REFERENCE = {
  year: 2025,
  city: 'WUP2025 DEGURBA urban-center estimate, 1 July',
  country: 'WPP2024 medium-variant projection, 1 July',
  citySource: 'https://population.un.org/wup/downloads?tab=Cities',
  countrySource: 'https://population.un.org/wpp/',
} as const;
const byPopulation = (
  a: { readonly name: string; readonly population: number },
  b: { readonly name: string; readonly population: number },
): number => b.population - a.population || a.name.localeCompare(b.name, 'en');
export function placeSections(
  country: Country,
): readonly { readonly name: string; readonly codes: readonly string[] }[] {
  return (['city', 'nationwide', 'regional'] as const)
    .map((scope) => ({
      name: { city: 'Cities', nationwide: 'Nationwide / cross-border', regional: 'Regional / cross-border' }[scope],
      codes: country.codes
        .filter((code) => PLACE_GEOGRAPHY[code]?.scope === scope)
        .toSorted((a, b) => {
          const x = PLACE_GEOGRAPHY[a];
          const y = PLACE_GEOGRAPHY[b];
          if (x === undefined || y === undefined) throw new Error('Missing geographic metadata');
          return (y.population ?? 0) - (x.population ?? 0) || x.name.localeCompare(y.name, 'en');
        }),
    }))
    .filter((section: { readonly name: string; readonly codes: readonly string[] }) => section.codes.length > 0);
}
export const CONTINENTS: readonly Continent[] = (
  [
    {
      name: 'Asia',
      population: 4835320060,
      countries: [
        {
          name: 'Japan',
          codes: ['tokyo'],
          population: 123103479,
          iso3: 'JPN',
        },
        {
          name: 'Hong Kong',
          codes: ['hk'],
          population: 7396076,
          iso3: 'HKG',
        },
      ],
    },
    {
      name: 'Europe',
      population: 744398832,
      countries: [
        {
          name: 'Austria',
          codes: ['vie'],
          population: 9113574,
          iso3: 'AUT',
        },
        {
          name: 'Czechia',
          codes: ['prg'],
          population: 10609239,
          iso3: 'CZE',
        },
        {
          name: 'Finland',
          codes: ['hel'],
          population: 5623329,
          iso3: 'FIN',
        },
        {
          name: 'France',
          codes: ['fr', 'par'],
          population: 66650804,
          iso3: 'FRA',
        },
        {
          name: 'Germany',
          codes: [
            'db',
            'agb',
            'ber',
            'bre',
            'cgn',
            'drs',
            'fra',
            'fre',
            'ham',
            'han',
            'kar',
            'lej',
            'muc',
            'nue',
            'rnk',
            'ruhr',
            'str',
          ],
          population: 84075075,
          iso3: 'DEU',
        },
        {
          name: 'Hungary',
          codes: ['bud'],
          population: 9632287,
          iso3: 'HUN',
        },
        {
          name: 'Ireland',
          codes: ['dub'],
          population: 5308039,
          iso3: 'IRL',
        },
        {
          name: 'Italy',
          codes: ['rom', 'vce'],
          population: 59146260,
          iso3: 'ITA',
        },
        {
          name: 'Netherlands',
          codes: ['nl'],
          population: 18346819,
          iso3: 'NLD',
        },
        {
          name: 'Norway',
          codes: ['no', 'osl'],
          population: 5623071,
          iso3: 'NOR',
        },
        {
          name: 'Poland',
          codes: ['gdn', 'krk', 'poz', 'waw'],
          population: 38140910,
          iso3: 'POL',
        },
        {
          name: 'Portugal',
          codes: ['lis'],
          population: 10411834,
          iso3: 'PRT',
        },
        {
          name: 'Spain',
          codes: ['ovd', 'bcn', 'bio', 'xry', 'mad', 'agp', 'alc', 'sdr', 'eas', 'svq', 'vlc', 'zaz'],
          population: 47889958,
          iso3: 'ESP',
        },
        {
          name: 'Sweden',
          codes: ['se', 'got', 'mal', 'sto'],
          population: 10656633,
          iso3: 'SWE',
        },
        {
          name: 'Switzerland',
          codes: ['zurich', 'geneva', 'basel', 'lausanne', 'bern', 'lugano', 'ch'],
          population: 8967407,
          iso3: 'CHE',
        },
        {
          name: 'United Kingdom',
          codes: ['gb', 'lon', 'man'],
          population: 69551332,
          iso3: 'GBR',
        },
      ],
    },
    {
      name: 'North America',
      population: 617311580,
      countries: [
        {
          name: 'United States',
          codes: ['amtrak', 'sfbay', 'bos', 'chi', 'den', 'msp', 'nyc', 'phl', 'pdx', 'sea'],
          population: 347275807,
          iso3: 'USA',
        },
        {
          name: 'Canada',
          codes: ['yyc', 'mtl', 'tor'],
          population: 40126723,
          iso3: 'CAN',
        },
      ],
    },
    {
      name: 'Oceania',
      population: 46609644,
      countries: [
        {
          name: 'Australia',
          codes: ['adl', 'bne', 'cbr', 'mel', 'per', 'nsw'],
          population: 26974026,
          iso3: 'AUS',
        },
      ],
    },
  ] as readonly Continent[]
)
  .toSorted(byPopulation)
  .map((continent) => ({
    name: continent.name,
    population: continent.population,
    countries: continent.countries.toSorted(byPopulation).map((country) => ({
      name: country.name,
      population: country.population,
      iso3: country.iso3,
      codes: placeSections(country).flatMap((section) => section.codes),
    })),
  }));
